/**
 * Editor operations for the attachment-backed card / comment `fileEmbed`
 * node — the inline chip for NON-IMAGE files (pdf / zip / csv / …). The
 * file sibling of `card-image-embed.ts`.
 *
 * The composer owns the upload lifecycle; these helpers own the
 * in-editor node lifecycle:
 *
 *   - `insertCardFilePlaceholder` — insert an inline file chip at the
 *     caret showing the filename + spinner (no `attachmentId` yet), and
 *     return a placeholder id (or `null` when the editor isn't mounted /
 *     editable).
 *   - `resolveCardFilePlaceholder` — swap in the durable `attachmentId`
 *     once the upload lands.
 *   - `markCardFilePlaceholderFailed` — flip the chip into (or out of) its
 *     in-place error state (Retry / Dismiss) when the upload fails; the
 *     node stays in the body so the author can act on it.
 *   - `failCardFilePlaceholder` — remove the placeholder outright (the card
 *     description surfaces on failure; the comment composers on Dismiss /
 *     cancel).
 *
 * `fileEmbed` is INLINE + atomic, so the chip drops at the caret inside
 * the current paragraph — no flanking paragraphs needed.
 */

import type { Editor } from '@tiptap/react'

import { collapseNodeSelectionAfter } from '../node-selection'

export interface CardFilePlaceholderMeta {
  fileName: string
  sizeBytes: number
  mimeType: string
}

let placeholderCounter = 0

function makePlaceholderId(): string {
  placeholderCounter += 1
  return `card-file-${Date.now()}-${placeholderCounter}`
}

/**
 * Insert an inline file-embed placeholder rendering the filename +
 * spinner. Returns the placeholder id, or `null` when the editor isn't
 * mounted / editable.
 */
export function insertCardFilePlaceholder(
  editor: Editor | null,
  meta: CardFilePlaceholderMeta,
): string | null {
  if (!editor || editor.isDestroyed || !editor.isEditable) return null
  const placeholderId = makePlaceholderId()
  editor
    .chain()
    .focus()
    // See `collapseNodeSelectionAfter` — guards both the "editor just
    // opened on a body starting with a block atom" case (insert would
    // replace it) and the multi-file case (next insert would replace the
    // placeholder just added).
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .insertContent({
      type: 'fileEmbed',
      attrs: {
        attachmentId: null,
        fileName: meta.fileName,
        sizeBytes: meta.sizeBytes,
        mimeType: meta.mimeType,
        uploadingPlaceholderId: placeholderId,
      },
    })
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .run()
  return placeholderId
}

/**
 * Swap a placeholder for its resolved attachment. Locates the node fresh
 * inside the transaction so an intervening edit (the upload is async)
 * doesn't stale the position.
 */
export function resolveCardFilePlaceholder(
  editor: Editor | null,
  placeholderId: string,
  result: { attachmentId: string },
): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      let targetPos = -1
      let targetAttrs: Record<string, unknown> | null = null
      state.doc.descendants((n, pos) => {
        if (targetPos !== -1) return false
        if (n.type.name === 'fileEmbed' && n.attrs.uploadingPlaceholderId === placeholderId) {
          targetPos = pos
          targetAttrs = n.attrs
          return false
        }
        return true
      })
      if (targetPos === -1 || !targetAttrs) return false
      // TS control-flow can't see the assignment inside the `descendants`
      // callback, so it still treats `targetAttrs` as `null` here despite
      // the guard above — cast past it.
      const attrs = targetAttrs as Record<string, unknown>
      if (dispatch) {
        tr.setNodeMarkup(targetPos, undefined, {
          ...attrs,
          attachmentId: result.attachmentId,
          uploadingPlaceholderId: null,
        })
      }
      return true
    })
    .run()
}

/**
 * Flip a chip's error state. `true` after the upload fails (the NodeView
 * renders the error chip + Retry / Dismiss), `false` when a Retry re-runs
 * the upload against the SAME node. A no-op when the author already
 * deleted the node.
 */
export function markCardFilePlaceholderFailed(
  editor: Editor | null,
  placeholderId: string,
  failed: boolean,
): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      let targetPos = -1
      let targetAttrs: Record<string, unknown> | null = null
      state.doc.descendants((n, pos) => {
        if (targetPos !== -1) return false
        if (n.type.name === 'fileEmbed' && n.attrs.uploadingPlaceholderId === placeholderId) {
          targetPos = pos
          targetAttrs = n.attrs
          return false
        }
        return true
      })
      if (targetPos === -1 || !targetAttrs) return false
      const attrs = targetAttrs as Record<string, unknown>
      if (dispatch) tr.setNodeMarkup(targetPos, undefined, { ...attrs, uploadingFailed: failed })
      return true
    })
    .run()
}

/** Remove a placeholder — failed upload (card surfaces) or Dismiss. */
export function failCardFilePlaceholder(editor: Editor | null, placeholderId: string): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      let targetPos = -1
      let targetSize = 0
      state.doc.descendants((n, pos) => {
        if (targetPos !== -1) return false
        if (n.type.name === 'fileEmbed' && n.attrs.uploadingPlaceholderId === placeholderId) {
          targetPos = pos
          targetSize = n.nodeSize
          return false
        }
        return true
      })
      if (targetPos === -1) return false
      if (dispatch) tr.delete(targetPos, targetPos + targetSize)
      return true
    })
    .run()
}
